import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import Seo, { absUrl } from "@/components/Seo";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { Reveal } from "../components/landing/Reveal";
import { useBooking } from "../context/BookingContext";
import { optImg } from "../lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Minimal renderer: "## " lines become h2, blank-line blocks become paragraphs.
const renderContent = (text) =>
  text.split(/\n\s*\n/).map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith("## ")) {
      return (
        <h2 key={i} className="mt-10 font-display font-bold text-2xl sm:text-3xl text-[#0b132b]" data-testid={`blog-h2-${i}`}>
          {trimmed.slice(3)}
        </h2>
      );
    }
    return (
      <p key={i} className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
        {trimmed}
      </p>
    );
  });

export const BlogPostPage = () => {
  const { slug } = useParams();
  const { openBooking } = useBooking();
  const [post, setPost] = useState(null);
  const [others, setOthers] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    setPost(null);
    axios
      .get(`${API}/posts/${slug}`)
      .then(({ data }) => {
        setPost(data);
        setState("ok");
      })
      .catch(() => setState("404"));
    axios.get(`${API}/posts`).then(({ data }) => setOthers(data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-post-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--brand)]" />
      </div>
    );
  }

  if (state === "404" || !post) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="blog-post-not-found">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-5 pt-32 pb-20 text-center">
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#0b132b]">Article not found</h1>
          <Link
            to="/blog"
            data-testid="blog-post-back-404"
            className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-6 py-3 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageUrl = window.location.href;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.image ? [absUrl(post.image)] : undefined,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: "Voyage Switzerland", url: window.location.origin },
    publisher: {
      "@type": "Organization",
      name: "Voyage Switzerland",
      url: window.location.origin,
      logo: { "@type": "ImageObject", url: absUrl("/logo.png") },
    },
    mainEntityOfPage: pageUrl,
  };

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={`${post.title} | Voyage Switzerland`}
        siteName="Voyage Switzerland"
        description={post.excerpt}
        image={post.image || "/og-image.png"}
        type="article"
        jsonLd={jsonLd}
        alternates={{ en: `/blog/${post.slug}` }}
        lang="en"
      />
      <Navbar />

      <article className="pt-[110px] sm:pt-[130px] pb-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[var(--brand)] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Travel Guide
          </Link>
          <Reveal>
            <p className="mt-6 text-xs font-bold uppercase tracking-widest text-[var(--brand)]">
              {new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })} · Voyage Switzerland Guide Team
            </p>
            <h1 className="mt-3 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0b132b] leading-[1.1]" data-testid="blog-post-title">
              {post.title}
            </h1>
            <p className="mt-4 text-lg text-slate-500 leading-relaxed" data-testid="blog-post-excerpt">
              {post.excerpt}
            </p>
          </Reveal>
          {post.image && (
            <Reveal delay={0.1}>
              <div className="mt-8 rounded-3xl overflow-hidden shadow-[0_24px_60px_-24px_rgba(11,19,43,0.25)]">
                <img src={optImg(post.image, 1600)} alt={post.title} className="w-full h-[260px] sm:h-[400px] object-cover" />
              </div>
            </Reveal>
          )}
          <Reveal delay={0.15}>
            <div className="mt-8" data-testid="blog-post-content">{renderContent(post.content)}</div>
          </Reveal>

          {/* CTA */}
          <Reveal className="mt-14">
            <div className="rounded-3xl bg-[#0b132b] p-7 sm:p-9 text-white" data-testid="blog-post-cta">
              <h2 className="font-display font-bold text-xl sm:text-2xl">Ready to see it yourself?</h2>
              <p className="mt-2 text-sm text-slate-300">
                Our private tours turn articles into memories — door to door, at your pace.
              </p>
              <button
                data-testid="blog-post-book-button"
                onClick={() => openBooking({ mode: "tour" })}
                className="group mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] hover:-translate-y-0.5"
              >
                Book Your Tour <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </article>

      {/* More articles */}
      {others.length > 0 && (
        <section className="py-16 bg-[#f8fafc] border-t border-slate-100" data-testid="blog-post-more">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#0b132b]">Keep reading</h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {others.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-more-${p.slug}`}
                  className="group block rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] hover:shadow-[0_30px_60px_-24px_rgba(225,29,72,0.35)] hover:border-[var(--brand)]/25 transition-all duration-300 hover:-translate-y-1.5"
                >
                  {p.image && (
                    <div className="h-36 overflow-hidden">
                      <img src={optImg(p.image, 800)} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-display font-bold text-base text-[#0b132b] leading-tight group-hover:text-[var(--brand)] transition-colors">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};
